import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

export interface ConfirmOptions {
  /** Düymənin mətni (boş — «Təsdiqlə»). */
  confirmLabel?: string;
  /** Ləğv düyməsinin mətni (boş — «Geri»). */
  cancelLabel?: string;
  /** Təhlükəli əməliyyat (silmə, ləğv) — təsdiq düyməsi qırmızı olur. */
  danger?: boolean;
}

type Confirm = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

interface Pending {
  message: string;
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

// Tətbiqin öz təsdiq pəncərəsi. window.confirm əvəzinə: useConfirm() → await confirm('…').
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<Confirm>(
    (message, options = {}) =>
      new Promise<boolean>((resolve) => {
        // Açıq pəncərə varsa, onu ləğv kimi bağla — iki dialoq üst-üstə düşməsin.
        setPending((cur) => {
          cur?.resolve(false);
          return { message, options, resolve };
        });
      }),
    [],
  );

  function answer(ok: boolean) {
    pending?.resolve(ok);
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <ConfirmBox pending={pending} onAnswer={answer} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmBox({ pending, onAnswer }: { pending: Pending; onAnswer: (ok: boolean) => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAnswer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onAnswer]);

  const { message, options } = pending;
  return (
    <div className="confirm-overlay">
      <div className="confirm-box card" role="alertdialog" aria-modal="true" aria-describedby="confirm-message">
        <p id="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button ref={cancelRef} type="button" className="btn-small" onClick={() => onAnswer(false)}>
            {options.cancelLabel ?? 'Geri'}
          </button>
          <button
            type="button"
            className={options.danger ? 'btn-small btn-danger' : 'btn-primary'}
            onClick={() => onAnswer(true)}
          >
            {options.confirmLabel ?? 'Təsdiqlə'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm: ConfirmProvider yoxdur');
  return confirm;
}
